import { Alert, InputNumber, Segmented, Select } from 'antd';
import { InputSelect, InputSwitch } from '../Input';
import { allowedCodes, defaultCodeOf, formatAmount, toMinor } from '../../utils/currency';
import { allCurrencyOptionGroups, currencyOption, currencySearchFilter } from '../../utils/money';
import ConversionUnitField from './ConversionUnitField';

/**
 * The card-definition sub-editor for a `currency` element's options —
 * everything the shared normaliser and formatter read (docs/plans/
 * currency.md, "Element definition"):
 *
 *   currencies       the allowlist, at least one ISO code, in the author's
 *                    order (the entry picker keeps it). Searchable over the
 *                    whole registry: the ordinary currencies, then the nine
 *                    ISO fund codes under their own heading.
 *   defaultCurrency  what a new entry opens in and what a bare number means;
 *                    limited to the chosen currencies. Unset = the first.
 *   display.style    'symbol' ($12.50 — the narrow glyph when no other chosen
 *                    currency shares it) or 'code' (USD 12.50), with a live
 *                    sample in the element's own default currency.
 *   min / max        MAJOR-unit bounds on the amount in whichever currency it
 *                    is entered — there is no conversion, which the editor
 *                    says when more than one currency is chosen.
 *   nonNegative      refuse a negative amount — ON for a new currency element
 *                    (ruling D8; the card editor's type change sets it), and
 *                    written true or false, never left out.
 *   convertFrom      { currency } — only while a SAVED numeric element is
 *                    being switched to currency (`conversion` prop, planned by
 *                    utils/conversions.js): the currency its existing numbers
 *                    are in. A request-time instruction the API never stores.
 *
 * The API's `invalid_options` rules are mirrored inline (at least one
 * currency, the default among them, min ≤ max) so the author sees the
 * refusal before the round trip; an `invalid_options` save error naming this
 * element is shown here too. Writes go through the form's path-based
 * onChange (`elements[i].options.x`), so the editor holds no state.
 */

const STYLE_OPTIONS = ['symbol', 'code'];

const finiteOrUndefined = v => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** "$12.50" / "USD 12.50" for the element's default currency, in a style. */
const sampleFor = (options, style) => {
	const code = defaultCodeOf(options) || 'USD';
	const amount = toMinor(12.5, code, 'round');
	return amount === null ? '' : formatAmount(amount, code, options, { style });
};

export default function CurrencyOptions({ index, element, onChange, saveError = null, conversion = null, basePath = null }) {
	const options = element?.options || {};
	const prefix = `${basePath || `elements[${index}]`}.options`;
	const currencies = allowedCodes(options);
	const defaultCurrency = defaultCodeOf(options);
	const style = options.display?.style === 'code' ? 'code' : 'symbol';

	const min = finiteOrUndefined(options.min);
	const max = finiteOrUndefined(options.max);
	const boundsCross = min !== undefined && max !== undefined && min > max;
	const several = currencies.length > 1;

	const refusal = saveError?.code === 'invalid_options' && saveError.element && saveError.element === element?.name
		? saveError
		: null;

	// The list is the author's order; a default that falls out of it is
	// dropped (the first then stands in), never left pointing at nothing.
	const setCurrencies = (codes) => {
		const list = (codes || []).filter((code, i, all) => all.indexOf(code) === i);
		onChange(`${prefix}.currencies`, list);
		if (options.defaultCurrency && !list.includes(options.defaultCurrency)) {
			onChange(`${prefix}.defaultCurrency`, undefined);
		}
	};

	const setDefault = (_, code) => onChange(`${prefix}.defaultCurrency`, code || undefined);

	const setStyle = (next) => onChange(`${prefix}.display`, next === 'code' ? { style: 'code' } : { style: 'symbol' });

	const setBound = (key, v) => onChange(`${prefix}.${key}`, finiteOrUndefined(v));

	const boundSuffix = several ? null : <span className='input-passive-suffix'>{defaultCurrency}</span>;

	return (
		<div className='currency-options'>
			<div className='micro-label card-def-element__section'>Currency</div>

			{refusal ? (
				<Alert type='error' showIcon className='mb-2' message='The currency settings were refused' description={refusal.message || null} />
			) : null}

			{!currencies.length ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message='Choose at least one currency'
					description='An amount is always in a currency, so the element needs one to offer.'
				/>
			) : null}

			<div className='mb-2'>
				<label className='d-block' htmlFor={`${prefix}.currencies`}>
					Currencies <span className='required-indicator' aria-hidden='true'>*</span>
				</label>
				<Select
					id={`${prefix}.currencies`}
					mode='multiple'
					size='large'
					className='w-100'
					value={currencies}
					onChange={setCurrencies}
					options={allCurrencyOptionGroups()}
					filterOption={currencySearchFilter}
					placeholder='Search currencies'
					aria-label='Currencies'
				/>
				<div className='form-panel__hint'>
					The currencies an amount may be entered in. There is no conversion between them.
				</div>
			</div>

			{/* A saved number element becoming a currency: which currency its
			    stored numbers are in. Absent for new elements and unchanged types. */}
			{conversion?.key === 'convertFrom' ? (
				<ConversionUnitField
					index={index}
					elementName={element?.name}
					plan={conversion}
					onChange={onChange}
					saveError={saveError}
				/>
			) : null}

			{several ? (
				<InputSelect
					elementName={`${prefix}.defaultCurrency`}
					elementLabel='Default currency'
					value={defaultCurrency || undefined}
					onChange={setDefault}
					options={currencies.map(currencyOption).filter(Boolean)}
					allowClear={false}
					help='The currency new amounts start in, and what a bare number means.'
				/>
			) : null}

			<div className='mb-2'>
				<label className='d-block' id={`${prefix}.display.style-label`}>Show as</label>
				<Segmented
					aria-labelledby={`${prefix}.display.style-label`}
					value={style}
					onChange={setStyle}
					options={STYLE_OPTIONS.map(value => ({
						value,
						label: `${value === 'code' ? 'Code' : 'Symbol'} (${sampleFor(options, value)})`,
					}))}
				/>
			</div>

			{boundsCross ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message='The minimum is above the maximum'
					description='No amount could satisfy both, so nothing would ever save. Swap them, or clear one.'
				/>
			) : null}

			<div className='currency-options__row'>
				<div className='mb-2 currency-options__cell'>
					<label className='d-block' htmlFor={`${prefix}.min`}>Minimum</label>
					<InputNumber
						id={`${prefix}.min`}
						size='large'
						className='w-100'
						value={min ?? null}
						onChange={v => setBound('min', v)}
						suffix={boundSuffix}
						inputMode='decimal'
						controls={false}
						aria-label='Minimum'
					/>
				</div>
				<div className='mb-2 currency-options__cell'>
					<label className='d-block' htmlFor={`${prefix}.max`}>Maximum</label>
					<InputNumber
						id={`${prefix}.max`}
						size='large'
						className='w-100'
						value={max ?? null}
						onChange={v => setBound('max', v)}
						suffix={boundSuffix}
						inputMode='decimal'
						controls={false}
						aria-label='Maximum'
					/>
				</div>
			</div>
			{several ? (
				<div className='form-panel__hint mb-2'>
					Bounds apply to the amount in whichever currency it is entered — there is no conversion.
				</div>
			) : null}

			{/* true or false, never "absent": a new currency element starts with
			    this on (ruling D8 — the API writes it for one that does not say),
			    so turning it off must SAY false. */}
			<div className='card-def-element__toggles'>
				<InputSwitch
					elementName={`${prefix}.nonNegative`}
					elementLabel='No negative amounts'
					description='Refuse an amount below zero.'
					value={options.nonNegative === true}
					onChange={(name, v) => onChange(name, v === true)}
					first={true}
				/>
			</div>
		</div>
	);
}
